import { useCallback } from 'react';

import { useMutation } from '@lumapps/base-api/react-query';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { error } from '@lumapps/notifications';
import { pin, unpin } from '@lumapps/posts/api';
import { REFRESH_EVENTS } from '@lumapps/posts/constants';
import { RefreshPosts } from '@lumapps/posts/types';
import { useDispatch } from '@lumapps/redux/react';
import { useInvalidateSpacesQueries } from '@lumapps/spaces/hooks/useInvalidateSpacesQueries';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { PIN_POST_MAX_COUNT } from '../constants';
import { WIDGET_POST_LIST } from '../keys';
import { usePostListsRefresh } from './usePostListsRefresh';

export const usePostPin = (postId: string, communityId: string, refreshOverride?: RefreshPosts) => {
    const [isUnpinConfirmOpen, , closeUnpinConfirm, openUnpinConfirm] = useBooleanState(false);
    const { translateAndReplace } = useTranslate();
    const dispatch = useDispatch();
    const invalidateSpacesQueries = useInvalidateSpacesQueries();

    const refreshPostLists = usePostListsRefresh(refreshOverride);

    const pinMutation = useMutation({
        mutationFn: () => pin(postId, communityId),
        onSuccess: ({ data }) => {
            // Refetch the space data so its pinned resources reflect the pin
            invalidateSpacesQueries();
            refreshPostLists(REFRESH_EVENTS.POST_PIN, data);
        },
        onError: (err) => {
            // Status 413 means we exceeded the maximum number of pinned posts
            if (err.response?.status === 413) {
                dispatch(
                    error({
                        translate: translateAndReplace(WIDGET_POST_LIST.PIN_POST_COUNT_EXCEEDED_ERROR, {
                            count: PIN_POST_MAX_COUNT,
                        }),
                    }),
                );
            } else {
                dispatch(error({ translate: GLOBAL.GENERIC_ERROR }));
            }
        },
    });

    const unpinMutation = useMutation({
        mutationFn: () => unpin(postId, communityId),
        onSuccess: ({ data }) => {
            // Refetch the space data so its pinned resources reflect the unpin
            invalidateSpacesQueries();
            refreshPostLists(REFRESH_EVENTS.POST_UNPIN, data);
            closeUnpinConfirm();
        },
        onError: () => {
            dispatch(error({ translate: GLOBAL.GENERIC_ERROR }));
            closeUnpinConfirm();
        },
    });

    const { mutate: performPin } = pinMutation;
    const { mutate: performUnpin } = unpinMutation;

    const onPinPost = useCallback(() => performPin(), [performPin]);
    const onUnpinPost = useCallback(() => performUnpin(), [performUnpin]);

    const unpinConfirmDialogProps = {
        isOpen: isUnpinConfirmOpen,
        isLoading: unpinMutation.isLoading || pinMutation.isLoading,
        onConfirm: onUnpinPost,
        onClose: closeUnpinConfirm,
    };

    return {
        unpinConfirmDialogProps,
        openUnpinConfirm,
        onPinPost,
    };
};
